<!--
  EnumMultiSelect.vue — Chip-based multi-select for enum fields.

  Selected values render as removable chips; remaining values appear in
  an "Add…" dropdown that resets after each pick. Replaces the native
  `<select multiple>` fallback for array-shape filter conditions on
  enum fields, where the native widget is hard to use and cosmetically
  inconsistent with the rest of the chip inputs (UsersInput, TagsInput,
  RecordsInput).
-->
<template>
  <div class="enum-multi-select input chip-input" :class="`is-${size}`">
    <span v-for="opt in selectedOptions" :key="opt.value" class="tag tag-link tag-sm">
      <span class="font-semibold">{{ opt.label }}</span>
      <button
        v-if="!readonly"
        type="button"
        class="delete is-small"
        :aria-label="`Remove ${opt.label}`"
        @click="remove(opt.value)"
      ></button>
    </span>

    <div v-if="!readonly && availableOptions.length" class="select is-small enum-multi-select__add">
      <select :value="''" @change="onAdd">
        <option value="" disabled>{{ placeholder }}</option>
        <option v-for="opt in availableOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { selectOptions } from "@auger/core/fields"

interface Props {
  modelValue?: Array<string | number> | null
  // `values` is the canonical `Field.values` shape (`readonly unknown[]`):
  // a list of scalar choices. selectOptions narrows and labels them.
  field?: { values?: readonly unknown[] } | null
  placeholder?: string
  size?: string
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  field: null,
  placeholder: "Add…",
  size: "normal",
  readonly: false,
})

const emit = defineEmits<{
  "update:modelValue": [value: Array<string | number>]
}>()

const allOptions = computed(() => selectOptions(props.field ?? {}))

const currentValues = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))

const selectedOptions = computed(() =>
  // Preserve modelValue order so the chip order matches the user's pick order.
  currentValues.value.map(
    (v) => allOptions.value.find((o) => o.value === v) ?? { value: v, label: String(v) }
  )
)

const availableOptions = computed(() =>
  allOptions.value.filter((o) => !currentValues.value.includes(o.value))
)

function remove(value: string | number) {
  emit(
    "update:modelValue",
    currentValues.value.filter((v) => v !== value)
  )
}

function onAdd(event: Event) {
  const target = event.target as HTMLSelectElement
  const picked = target.value
  target.value = "" // reset so the same option can't sit highlighted in the dropdown
  if (!picked) return
  // <select>.value is always a string; look up the option by string-comparing
  // and emit the option's NATIVE value type. Without this, numeric enums
  // (`field.values: [1, 2, 3]`) would round-trip as strings and never
  // reconcile with modelValue (1 !== "1") — chip falls back to garbage label
  // and the option stays in the dropdown forever.
  const opt = allOptions.value.find((o) => String(o.value) === picked)
  if (!opt) return
  if (currentValues.value.includes(opt.value)) return
  emit("update:modelValue", [...currentValues.value, opt.value])
}
</script>

<style>
.enum-multi-select {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-height: var(--auger-input-height-sm, 1.75rem);
  padding: 2px 4px;

  .tag.is-link {
    .delete {
      margin-inline-start: 6px;
    }
  }

  .enum-multi-select__add {
    flex: 0 0 auto;

    select {
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0 1.5rem 0 0.25rem;
      height: auto;
      font-size: inherit;
      color: var(--auger-text-muted);
    }
  }
}
</style>
